import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import { buildLessonAudioPayload, canPlayAudioUrl, lessonAudioAttachmentFileIdFromUrl, normalizeLessonAudioUrl, serializeLessonAudioForBaseline } from '../../../helpers/audioLesson';
import { canPlayVideoUrl } from '../../../helpers/videoEmbed';
import { toast } from 'react-toastify';
import { makeApiRequests, readFile, uploadCourseAttachment } from '../../../helpers/api';
import { API_URL, ENDPOINTS } from '../../../helpers/constants';
import AlertModal from '../../AlertModal';
import CourseTrainingLessonView from './CourseTrainingLessonView';
import CourseTrainingEditorSidebar from './CourseTrainingEditorSidebar';
import { attachmentId, progressiveCourseAttachmentStreamUrl } from './courseTrainingUtils';

function CourseTrainingEditor({
  courseId = null,
  viewOnly = false,
  courses = [],
  canManageCourses = false,
  onCourseDetailLoaded,
  onExitEditor
}) {
  const history = useHistory();
  const listCourse = courseId ? courses.find(c => String(c._id ?? c.id ?? '') === courseId) : null;
  const [localCourse, setLocalCourse] = useState(null);
  const [detailLoading, setDetailLoading] = useState(Boolean(courseId));
  const [courseRevision, setCourseRevision] = useState(0);
  const [lessonSaveBaseline, setLessonSaveBaseline] = useState('');
  const [currentLessonId, setCurrentLessonId] = useState('');
  const [completedLessons, setCompletedLessons] = useState([]);
  const [expandedModules, setExpandedModules] = useState([]);
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [uploadingLessonVideo, setUploadingLessonVideo] = useState(false);
  const [uploadingLessonAudio, setUploadingLessonAudio] = useState(false);
  const [uploadingLessonAttachment, setUploadingLessonAttachment] = useState(false);
  const [addingModule, setAddingModule] = useState(false);
  const [addingLessonModuleId, setAddingLessonModuleId] = useState('');
  const [reorderingStructure, setReorderingStructure] = useState(false);
  const [togglingLessonComplete, setTogglingLessonComplete] = useState(false);
  const [isLargeViewport, setIsLargeViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 992px)').matches
  );

  const lessonVideoFileInputRef = useRef(null);
  const lessonAudioFileInputRef = useRef(null);
  const lessonQuillWrapRef = useRef(null);
  const navGuardRef = useRef({ skip: false, pending: null });
  const shellRef = useRef(null);

  const mergedCourse = useMemo(() => {
    if (!courseId) return null;
    if (localCourse && String(localCourse._id ?? localCourse.id ?? '') === courseId) return localCourse;
    return listCourse;
  }, [courseId, localCourse, listCourse]);

  const editMode = Boolean(courseId && mergedCourse) && !viewOnly && canManageCourses;

  const modules = mergedCourse?.modules || [];

  const currentLesson = useMemo(() => {
    for (const m of modules) {
      const found = m.lessons.find(l => String(l._id ?? l.id ?? '') === currentLessonId);
      if (found) return found;
    }
    return modules[0]?.lessons[0];
  }, [modules, currentLessonId]);

  const currentModuleId = useMemo(() => {
    const mod = modules.find(m => m.lessons.some(l => String(l._id ?? l.id ?? '') === currentLessonId));
    return mod == null ? '' : String(mod._id ?? mod.id ?? '');
  }, [modules, currentLessonId]);

  const normalizedLessonVideoUrl = useMemo(() => {
    const raw = currentLesson?.videoUrl;
    if (raw == null) return '';
    const t = String(raw).trim();
    if (!t) return '';
    if (/^https?:\/\//i.test(t)) return t;
    if (t.startsWith('//')) return `https:${t}`;
    return `https://${t}`;
  }, [currentLesson?.videoUrl]);

  /** Matches both legacy `/attachments/:id` and progressive `/attachments/:id/stream`. */
  const courseAttachmentStreamPrefix = useMemo(
    () => (courseId ? `${API_URL}/courses/${courseId}/attachments/` : ''),
    [courseId]
  );

  const isLessonVideoCourseAttachmentStream = useMemo(
    () =>
      Boolean(
        normalizedLessonVideoUrl &&
          courseId &&
          String(normalizedLessonVideoUrl).startsWith(courseAttachmentStreamPrefix)
      ),
    [normalizedLessonVideoUrl, courseId, courseAttachmentStreamPrefix]
  );

  const videoAttachmentFileId = useMemo(() => {
    if (!normalizedLessonVideoUrl) return null;
    return lessonAudioAttachmentFileIdFromUrl(normalizedLessonVideoUrl);
  }, [normalizedLessonVideoUrl]);

  const normalizedLessonAudioUrl = useMemo(
    () => normalizeLessonAudioUrl(currentLesson?.audio?.url),
    [currentLesson?.audio?.url]
  );

  const isLessonAudioCourseAttachmentStream = useMemo(
    () =>
      Boolean(
        normalizedLessonAudioUrl &&
          courseId &&
          String(normalizedLessonAudioUrl).startsWith(courseAttachmentStreamPrefix)
      ),
    [normalizedLessonAudioUrl, courseId, courseAttachmentStreamPrefix]
  );

  const audioAttachmentFileId = useMemo(
    () => lessonAudioAttachmentFileIdFromUrl(normalizedLessonAudioUrl),
    [normalizedLessonAudioUrl]
  );

  const audioPlayUrl = useMemo(() => {
    const base = normalizedLessonAudioUrl || '';
    if (!base || !isLessonAudioCourseAttachmentStream) return base;
    return progressiveCourseAttachmentStreamUrl(base);
  }, [normalizedLessonAudioUrl, isLessonAudioCourseAttachmentStream]);

  const canPlayAudio = useMemo(() => {
    if (!audioPlayUrl) return false;
    if (isLessonAudioCourseAttachmentStream) return true;
    return canPlayAudioUrl(audioPlayUrl);
  }, [audioPlayUrl, isLessonAudioCourseAttachmentStream]);

  const lessonIsDirty = useMemo(() => {
    if (
      !editMode ||
      !String(currentLesson?._id ?? currentLesson?.id ?? '') ||
      !currentModuleId ||
      lessonSaveBaseline === ''
    )
      return false;
    const mod = modules.find(m => String(m._id ?? m.id ?? '') === currentModuleId);
    if (!mod) return false;
    return (
      JSON.stringify({
        lessonId: String(currentLesson._id ?? currentLesson.id ?? ''),
        title: currentLesson.title ?? '',
        description: currentLesson.description ?? '',
        videoUrl: currentLesson.videoUrl ?? '',
        platform: currentLesson.platform ?? '',
        audio: serializeLessonAudioForBaseline(currentLesson.audio),
        attachments: [...(currentLesson.attachments || [])]
          .map(a => (a != null && typeof a === 'object' ? String(a._id ?? a.id ?? '') : String(a ?? '')))
          .filter(Boolean)
          .sort(),
        moduleTitle: mod.title ?? '',
        moduleDescription: mod.description ?? ''
      }) !== lessonSaveBaseline
    );
  }, [editMode, currentLesson, currentModuleId, modules, lessonSaveBaseline]);

  const playUrl = useMemo(() => {
    const base = normalizedLessonVideoUrl || '';
    if (!base || !isLessonVideoCourseAttachmentStream) return base;
    return progressiveCourseAttachmentStreamUrl(base);
  }, [normalizedLessonVideoUrl, isLessonVideoCourseAttachmentStream]);

  const canPlay = useMemo(() => {
    if (!playUrl) return false;
    if (isLessonVideoCourseAttachmentStream) return true;
    return canPlayVideoUrl(playUrl);
  }, [playUrl, isLessonVideoCourseAttachmentStream]);

  const visibleAttachments = useMemo(() => {
    const list = currentLesson?.attachments || [];
    const hidden = new Set([videoAttachmentFileId, audioAttachmentFileId].filter(Boolean));
    if (!hidden.size) return list;
    return list.filter(a => !hidden.has(attachmentId(a)));
  }, [currentLesson?.attachments, videoAttachmentFileId, audioAttachmentFileId]);

  const totalLessons = modules.reduce((acc, module) => acc + module.lessons.length, 0);
  const completedCount = completedLessons.length;
  const progressPercentage = totalLessons ? Math.round((completedCount / totalLessons) * 100) : 0;
  const displayCourseTitle = mergedCourse?.title || listCourse?.title || 'Course';
  const lessonMarkedComplete = currentLesson
    ? completedLessons.includes(String(currentLesson._id ?? currentLesson.id ?? ''))
    : false;

  const reloadCourse = useCallback(async () => {
    if (!courseId) return;
    const courseRes = await makeApiRequests({
      method: 'GET',
      endpoint: ENDPOINTS.COURSE_WITH_ID(courseId)
    });
    if (courseRes.error) {
      toast.error(courseRes.error);
      return;
    }
    const modsRes = await makeApiRequests({
      endpoint: ENDPOINTS.COURSE_MODULES_SEARCH(courseId),
      requestBody: { sort: { order: 1 } }
    });
    if (modsRes.error) {
      toast.error(modsRes.error);
      return;
    }
    const modList = modsRes.response.results || [];
    const modulesWithLessons = await Promise.all(
      modList.map(async m => {
        const lesRes = await makeApiRequests({
          endpoint: ENDPOINTS.COURSE_MODULE_LESSONS_SEARCH(String(m._id)),
          requestBody: { sort: { order: 1 } }
        });
        return { ...m, lessons: lesRes.error ? [] : lesRes.response.results || [] };
      })
    );
    const response = {
      ...courseRes.response,
      modules: modulesWithLessons,
      allowedRoles: courseRes.response.allowedRoles || []
    };
    setLocalCourse(response);
    setCourseRevision(r => r + 1);
    if (onCourseDetailLoaded) onCourseDetailLoaded(response);
  }, [courseId, onCourseDetailLoaded]);

  const saveCurrentLesson = useCallback(async () => {
    if (!String(currentLesson?._id ?? currentLesson?.id ?? '') || !currentModuleId) return false;
    setSavingLesson(true);
    try {
      const mod = modules.find(m => String(m._id ?? m.id ?? '') === currentModuleId);
      if (mod) {
        const { error: modErr } = await makeApiRequests({
          method: 'PUT',
          endpoint: ENDPOINTS.MODULE_WITH_ID(currentModuleId),
          requestBody: {
            title: (mod.title && String(mod.title).trim()) || 'Untitled module',
            description: mod.description != null && String(mod.description).trim().length ? mod.description : ' '
          }
        });
        if (modErr) {
          toast.error(modErr);
          return false;
        }
      }
      const atts = (currentLesson.attachments || []).map(attachmentId).filter(Boolean);
      const { error } = await makeApiRequests({
        method: 'PUT',
        endpoint: ENDPOINTS.LESSON_WITH_ID(String(currentLesson?._id ?? currentLesson?.id ?? '')),
        requestBody: {
          title: (currentLesson.title && String(currentLesson.title).trim()) || 'Untitled lesson',
          description:
            currentLesson.description != null && String(currentLesson.description).trim().length
              ? currentLesson.description
              : ' ',
          videoUrl: currentLesson.videoUrl || '',
          platform: currentLesson.platform || '',
          audio: buildLessonAudioPayload(currentLesson.audio),
          attachments: atts
        }
      });
      if (error) {
        toast.error(error);
        return false;
      }
      const modAfter = modules.find(m => String(m._id ?? m.id ?? '') === currentModuleId);
      if (modAfter) {
        setLessonSaveBaseline(
          JSON.stringify({
            lessonId: String(currentLesson._id ?? currentLesson.id ?? ''),
            title: currentLesson.title ?? '',
            description: currentLesson.description ?? '',
            videoUrl: currentLesson.videoUrl ?? '',
            platform: currentLesson.platform ?? '',
            audio: serializeLessonAudioForBaseline(currentLesson.audio),
            attachments: [...(currentLesson.attachments || [])]
              .map(a => (a != null && typeof a === 'object' ? String(a._id ?? a.id ?? '') : String(a ?? '')))
              .filter(Boolean)
              .sort(),
            moduleTitle: modAfter.title ?? '',
            moduleDescription: modAfter.description ?? ''
          })
        );
      }
      toast.success('Lesson saved');
      return true;
    } finally {
      setSavingLesson(false);
    }
  }, [currentLesson, currentModuleId, modules]);

  const updateCourse = fn => {
    if (!courseId) return;
    setLocalCourse(prev => {
      if (!prev || String(prev._id ?? prev.id ?? '') !== courseId) return prev;
      return fn(prev);
    });
  };

  const updateModuleTitle = (moduleId, title) => {
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m => (String(m._id ?? m.id ?? '') === moduleId ? { ...m, title } : m))
    }));
  };

  const revertLessonToBaseline = useCallback(() => {
    if (!lessonSaveBaseline || !currentLessonId || !currentModuleId) return;
    let baseline;
    try {
      baseline = JSON.parse(lessonSaveBaseline);
    } catch {
      return;
    }
    const lessonId = String(baseline.lessonId ?? '');
    const baselineAttIds = new Set(baseline.attachments || []);
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m => {
        const moduleId = String(m._id ?? m.id ?? '');
        const module =
          moduleId === currentModuleId
            ? {
                ...m,
                title: baseline.moduleTitle ?? m.title,
                description: baseline.moduleDescription ?? m.description
              }
            : m;
        return {
          ...module,
          lessons: module.lessons.map(l => {
            if (String(l._id ?? l.id ?? '') !== lessonId) return l;
            const keptAttachments = (l.attachments || []).filter(a => baselineAttIds.has(attachmentId(a)));
            return {
              ...l,
              title: baseline.title ?? '',
              description: baseline.description ?? '',
              videoUrl: baseline.videoUrl ?? '',
              platform: baseline.platform ?? '',
              audio: baseline.audio || undefined,
              attachments: keptAttachments.length ? keptAttachments : undefined
            };
          })
        };
      })
    }));
  }, [lessonSaveBaseline, currentLessonId, currentModuleId]);

  const setPendingNav = target => {
    navGuardRef.current.pending = target;
    setPendingNavigation(target);
  };

  const clearPendingNav = () => {
    navGuardRef.current.pending = null;
    setPendingNavigation(null);
  };

  useEffect(() => {
    if (!editMode || !lessonIsDirty) return undefined;
    const onBeforeUnload = e => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [editMode, lessonIsDirty]);

  const completeNavigation = target => {
    if (target.lessonId != null) {
      setCurrentLessonId(target.lessonId);
      return;
    }
    navGuardRef.current.skip = true;
    if (target.location) history.push(target.location);
    else onExitEditor();
  };

  const guardNavigation = target => {
    if (editMode && lessonIsDirty) setPendingNav(target);
    else completeNavigation(target);
  };

  const handleSelectLesson = lessonId => {
    const nextId = String(lessonId ?? '');
    const currentId = String(currentLesson?._id ?? currentLesson?.id ?? '');
    if (!nextId || nextId === currentId) return;
    guardNavigation({ lessonId: nextId });
  };

  const handleExitEditor = () => guardNavigation({});

  const handleUnsavedNavigationCancel = () => {
    if (!savingLesson) clearPendingNav();
  };

  const handleUnsavedNavigationDiscard = () => {
    if (!pendingNavigation || savingLesson) return;
    const target = pendingNavigation;
    revertLessonToBaseline();
    clearPendingNav();
    completeNavigation(target);
  };

  const handleUnsavedNavigationSave = async () => {
    if (!pendingNavigation || savingLesson) return;
    const target = pendingNavigation;
    if (!(await saveCurrentLesson())) return;
    clearPendingNav();
    completeNavigation(target);
  };

  const performRemoveModule = async moduleId => {
    const { error } = await makeApiRequests({
      method: 'DELETE',
      endpoint: ENDPOINTS.MODULE_WITH_ID(moduleId)
    });
    if (error) {
      toast.error(error);
      return false;
    }
    setEditingModuleId(prev => (prev === moduleId ? null : prev));
    await reloadCourse();
    toast.success('Module deleted');
    return true;
  };

  const addModule = async () => {
    if (!courseId || addingModule || addingLessonModuleId) return;
    setAddingModule(true);
    try {
      const nextOrder = modules.length > 0 ? Math.max(...modules.map(m => m.order || 0)) + 1 : 1;
      const { response: mod, error } = await makeApiRequests({
        method: 'POST',
        endpoint: ENDPOINTS.COURSE_MODULES(courseId),
        requestBody: {
          title: 'New module',
          description: ' ',
          order: nextOrder
        }
      });
      if (error) {
        toast.error(error);
        return;
      }
      const { error: lesErr } = await makeApiRequests({
        method: 'POST',
        endpoint: ENDPOINTS.COURSE_MODULE_LESSONS(String(mod._id)),
        requestBody: {
          title: 'New lesson',
          description: ' ',
          order: 1,
          videoUrl: '',
          platform: ''
        }
      });
      if (lesErr) {
        toast.error(lesErr);
        return;
      }
      await reloadCourse();
      const mid = String(mod._id);
      if (mid) setExpandedModules(prev => (prev.includes(mid) ? prev : [...prev, mid]));
    } finally {
      setAddingModule(false);
    }
  };

  const addLesson = async moduleId => {
    if (addingModule || addingLessonModuleId) return;
    setAddingLessonModuleId(moduleId);
    try {
      const mod = modules.find(m => String(m._id ?? m.id ?? '') === moduleId);
      const nextOrder = mod ? Math.max(0, ...mod.lessons.map(l => l.order || 0)) + 1 : 1;
      const { error } = await makeApiRequests({
        method: 'POST',
        endpoint: ENDPOINTS.COURSE_MODULE_LESSONS(moduleId),
        requestBody: {
          title: 'New lesson',
          description: ' ',
          order: nextOrder,
          videoUrl: '',
          platform: ''
        }
      });
      if (error) {
        toast.error(error);
        return;
      }
      setExpandedModules(prev => (prev.includes(moduleId) ? prev : [...prev, moduleId]));
      await reloadCourse();
    } finally {
      setAddingLessonModuleId('');
    }
  };

  const performRemoveLesson = async (moduleId, lessonId) => {
    const mod = modules.find(m => String(m._id ?? m.id ?? '') === moduleId);
    if (mod && mod.lessons.length <= 1) {
      toast.error('A module must keep at least one lesson.');
      return false;
    }
    const { error } = await makeApiRequests({
      method: 'DELETE',
      endpoint: ENDPOINTS.LESSON_WITH_ID(lessonId)
    });
    if (error) {
      toast.error(error);
      return false;
    }
    await reloadCourse();
    toast.success('Lesson deleted');
    return true;
  };

  const handleConfirmDeleteTarget = async () => {
    if (!deleteConfirm || deleteSubmitting) return;
    setDeleteSubmitting(true);
    let ok = false;
    if (deleteConfirm.type === 'module') {
      ok = await performRemoveModule(deleteConfirm.moduleId);
    } else {
      ok = await performRemoveLesson(deleteConfirm.moduleId, deleteConfirm.lessonId);
    }
    setDeleteSubmitting(false);
    if (ok) setDeleteConfirm(null);
  };

  const updateLessonField = (moduleId, lessonId, field, value) => {
    if (!moduleId) return;
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l => (String(l._id ?? l.id ?? '') === lessonId ? { ...l, [field]: value } : l))
            }
      )
    }));
  };

  const updateAttachmentsLocal = (moduleId, lessonId, attachments) => {
    if (!moduleId) return;
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l =>
                String(l._id ?? l.id ?? '') !== lessonId
                  ? l
                  : { ...l, attachments: attachments.length ? attachments : undefined }
              )
            }
      )
    }));
  };

  const addAttachmentFile = async (moduleId, lessonId, file) => {
    if (!courseId || !file) return;
    setUploadingLessonAttachment(true);
    try {
      const { response, error } = await uploadCourseAttachment(courseId, file);
      if (error) {
        toast.error(error);
        return;
      }
      const fid = response?.fileId;
      if (!fid) {
        toast.error('Upload did not return a file id. Check the course attachment API response shape.');
        return;
      }
      const lesson = modules.flatMap(m => m.lessons).find(l => String(l._id ?? l.id ?? '') === lessonId);
      const prev = lesson?.attachments || [];
      const next = [...prev, { _id: fid, filename: file.name }];
      updateAttachmentsLocal(moduleId, lessonId, next);
    } finally {
      setUploadingLessonAttachment(false);
    }
  };

  /** Upload video file: set lesson.videoUrl to ranged stream URL and add file id to attachments (required for access). */
  const applyLessonVideoUpload = (moduleId, lessonId, fileId) => {
    if (!courseId || !moduleId || !lessonId || !fileId) return;
    const streamUrl = `${API_URL}${ENDPOINTS.COURSE_ATTACHMENT_VIDEO_STREAM(courseId, fileId)}`;
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l => {
                if (String(l._id ?? l.id ?? '') !== lessonId) return l;
                const prev = l.attachments || [];
                const nextAtt = prev.some(a => attachmentId(a) === fileId) ? prev : [...prev, fileId];
                return {
                  ...l,
                  videoUrl: streamUrl,
                  attachments: nextAtt.length ? nextAtt : undefined
                };
              })
            }
      )
    }));
  };

  const uploadLessonVideoFile = async (moduleId, lessonId, file) => {
    if (!courseId || !file) return;
    setUploadingLessonVideo(true);
    try {
      const { response, error } = await uploadCourseAttachment(courseId, file);
      if (error) {
        toast.error(error);
        return;
      }
      const fid = response?.fileId;
      if (!fid) {
        toast.error('Upload did not return a file id.');
        return;
      }
      applyLessonVideoUpload(moduleId, lessonId, fid);
      toast.success('Video uploaded — save the lesson to persist.');
    } finally {
      setUploadingLessonVideo(false);
    }
  };

  /** Upload audio file: set lesson.audio.url to ranged stream URL and add file id to attachments. */
  const applyLessonAudioUpload = (moduleId, lessonId, fileId, fileMeta = {}) => {
    if (!courseId || !moduleId || !lessonId || !fileId) return;
    const streamUrl = `${API_URL}${ENDPOINTS.COURSE_ATTACHMENT_VIDEO_STREAM(courseId, fileId)}`;
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l => {
                if (String(l._id ?? l.id ?? '') !== lessonId) return l;
                const prev = l.attachments || [];
                const nextAtt = prev.some(a => attachmentId(a) === fileId) ? prev : [...prev, fileId];
                return {
                  ...l,
                  audio: {
                    url: streamUrl,
                    filename: fileMeta.filename || undefined,
                    size: fileMeta.size != null ? fileMeta.size : undefined
                  },
                  attachments: nextAtt.length ? nextAtt : undefined
                };
              })
            }
      )
    }));
  };

  const uploadLessonAudioFile = async (moduleId, lessonId, file) => {
    if (!courseId || !file) return;
    setUploadingLessonAudio(true);
    try {
      const { response, error } = await uploadCourseAttachment(courseId, file);
      if (error) {
        toast.error(error);
        return;
      }
      const fid = response?.fileId;
      if (!fid) {
        toast.error('Upload did not return a file id.');
        return;
      }
      applyLessonAudioUpload(moduleId, lessonId, fid, { filename: file.name, size: file.size });
      toast.success('Audio uploaded — save the lesson to persist.');
    } finally {
      setUploadingLessonAudio(false);
    }
  };

  const clearLessonAudio = (moduleId, lessonId) => {
    if (!moduleId || !lessonId) return;
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l => {
                if (String(l._id ?? l.id ?? '') !== lessonId) return l;
                const aid = lessonAudioAttachmentFileIdFromUrl(l.audio?.url);
                let nextAtt = l.attachments || [];
                if (aid) {
                  nextAtt = nextAtt.filter(a => attachmentId(a) !== aid);
                }
                const { audio: _a, ...rest } = l;
                return {
                  ...rest,
                  attachments: nextAtt.length ? nextAtt : undefined
                };
              })
            }
      )
    }));
  };

  const updateLessonAudioUrl = (moduleId, lessonId, url) => {
    if (!moduleId) return;
    const trimmed = url == null ? '' : String(url).trim();
    updateCourse(c => ({
      ...c,
      modules: c.modules.map(m =>
        String(m._id ?? m.id ?? '') !== moduleId
          ? m
          : {
              ...m,
              lessons: m.lessons.map(l => {
                if (String(l._id ?? l.id ?? '') !== lessonId) return l;
                if (!trimmed) {
                  const aid = lessonAudioAttachmentFileIdFromUrl(l.audio?.url);
                  let nextAtt = l.attachments || [];
                  if (aid) {
                    nextAtt = nextAtt.filter(a => attachmentId(a) !== aid);
                  }
                  const { audio: _a, ...rest } = l;
                  return { ...rest, attachments: nextAtt.length ? nextAtt : undefined };
                }
                return {
                  ...l,
                  audio: {
                    ...(l.audio && typeof l.audio === 'object' ? l.audio : {}),
                    url: trimmed
                  }
                };
              })
            }
      )
    }));
  };

  const removeAttachment = (moduleId, lessonId, index) => {
    if (!moduleId) return;
    const lesson = modules.flatMap(m => m.lessons).find(l => String(l._id ?? l.id ?? '') === lessonId);
    const next = [...(lesson?.attachments || [])];
    next.splice(index, 1);
    updateAttachmentsLocal(moduleId, lessonId, next);
  };

  const downloadAttachment = async fileId => {
    let objUrl;
    try {
      const { response, error, suggestedFilename, contentType } = await readFile({
        bucket: 'course',
        courseId,
        fileId,
        parseContentDisposition: true
      });
      if (error || !response) {
        toast.error('Could not download file');
        return;
      }
      objUrl = response;
      const sanitizeName = n => {
        if (!n) return 'download';
        return String(n)
          .replace(/[/\\?%*:|"<>]/g, '_')
          .slice(0, 180);
      };
      let downloadName = sanitizeName(suggestedFilename) || null;
      if (!downloadName || downloadName === 'download') {
        const m = contentType
          ? String(contentType)
              .split(';')[0]
              .trim()
              .toLowerCase()
          : '';
        const mimeMap = {
          'application/pdf': '.pdf',
          'image/jpeg': '.jpg',
          'image/png': '.png',
          'image/gif': '.gif',
          'image/webp': '.webp',
          'text/plain': '.txt',
          'application/zip': '.zip',
          'video/mp4': '.mp4',
          'video/webm': '.webm',
          'application/msword': '.doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx'
        };
        const ext = mimeMap[m] || '';
        downloadName = sanitizeName(`attachment-${String(fileId).slice(-8)}${ext}`);
      }
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = downloadName;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => {
        if (objUrl) URL.revokeObjectURL(objUrl);
      }, 30_000);
    } catch {
      toast.error('Could not download file');
      if (objUrl) URL.revokeObjectURL(objUrl);
    }
  };

  const toggleModule = moduleId => {
    setExpandedModules(prev => (prev.includes(moduleId) ? prev.filter(id => id !== moduleId) : [...prev, moduleId]));
  };

  const buildLessonReorderPayload = moduleList =>
    moduleList.map(mod => ({
      moduleId: String(mod._id ?? mod.id ?? ''),
      lessonIds: (mod.lessons || []).map(lesson => String(lesson._id ?? lesson.id ?? '')).filter(Boolean)
    }));

  const onStructureDragEnd = async result => {
    if (!editMode || !courseId || reorderingStructure) return;
    if (!result.destination) return;
    if (addingModule || addingLessonModuleId) return;

    const { type, source, destination } = result;
    if (source.index === destination.index && source.droppableId === destination.droppableId) return;

    if (type === 'MODULE') {
      const previousModules = modules.map(mod => ({ ...mod, lessons: [...(mod.lessons || [])] }));
      const nextModules = previousModules.map(mod => ({ ...mod, lessons: [...mod.lessons] }));
      const [moved] = nextModules.splice(source.index, 1);
      nextModules.splice(destination.index, 0, moved);
      updateCourse(c => ({ ...c, modules: nextModules }));

      setReorderingStructure(true);
      const { error } = await makeApiRequests({
        method: 'PUT',
        endpoint: ENDPOINTS.COURSE_MODULES_REORDER(courseId),
        requestBody: {
          moduleIds: nextModules.map(mod => String(mod._id ?? mod.id ?? '')).filter(Boolean)
        }
      });
      setReorderingStructure(false);
      if (error) {
        toast.error(error);
        updateCourse(c => ({ ...c, modules: previousModules }));
        return;
      }
      setCourseRevision(r => r + 1);
      return;
    }

    if (type === 'LESSON') {
      const sourceModuleId = source.droppableId.replace(/^lessons-/, '');
      const destModuleId = destination.droppableId.replace(/^lessons-/, '');
      const previousModules = modules.map(mod => ({ ...mod, lessons: [...(mod.lessons || [])] }));
      const nextModules = previousModules.map(mod => ({ ...mod, lessons: [...mod.lessons] }));
      const fromMod = nextModules.find(m => String(m._id ?? m.id ?? '') === sourceModuleId);
      const toMod = nextModules.find(m => String(m._id ?? m.id ?? '') === destModuleId);
      if (!fromMod || !toMod) return;

      const [movedLesson] = fromMod.lessons.splice(source.index, 1);
      toMod.lessons.splice(destination.index, 0, movedLesson);
      updateCourse(c => ({ ...c, modules: nextModules }));

      if (destModuleId && !expandedModules.includes(destModuleId)) {
        setExpandedModules(prev => [...prev, destModuleId]);
      }

      setReorderingStructure(true);
      const { error } = await makeApiRequests({
        method: 'PUT',
        endpoint: ENDPOINTS.COURSE_LESSONS_REORDER(courseId),
        requestBody: {
          modules: buildLessonReorderPayload(nextModules)
        }
      });
      setReorderingStructure(false);
      if (error) {
        toast.error(error);
        updateCourse(c => ({ ...c, modules: previousModules }));
        return;
      }
      setCourseRevision(r => r + 1);
    }
  };

  const toggleLessonComplete = async () => {
    if (!currentLesson || !currentModuleId || togglingLessonComplete) return;
    const mod = modules.find(m => String(m._id ?? m.id ?? '') === currentModuleId);
    if (!mod) return;
    setTogglingLessonComplete(true);
    try {
      const lessonIdsInModule = new Set(mod.lessons.map(l => String(l._id ?? l.id ?? '')));
      const doneInModule = completedLessons.filter(id => lessonIdsInModule.has(id));
      const nextForModule = doneInModule.includes(String(currentLesson?._id ?? currentLesson?.id ?? ''))
        ? doneInModule.filter(id => id !== String(currentLesson?._id ?? currentLesson?.id ?? ''))
        : [...doneInModule, String(currentLesson?._id ?? currentLesson?.id ?? '')];

      const { error } = await makeApiRequests({
        method: 'POST',
        endpoint: ENDPOINTS.MODULE_PROGRESS(currentModuleId),
        requestBody: { completedLessons: nextForModule }
      });
      if (error) {
        toast.error(error);
        return;
      }
      const { response: prog } = await makeApiRequests({
        method: 'GET',
        endpoint: ENDPOINTS.COURSE_PROGRESS(courseId)
      });
      const ids = (prog?.completedLessons || []).map(x =>
        x && typeof x === 'object' && x._id != null ? String(x._id) : String(x)
      );
      setCompletedLessons(ids.filter(Boolean));
    } finally {
      setTogglingLessonComplete(false);
    }
  };

  useEffect(() => {
    if (!courseId) {
      setLocalCourse(null);
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      const courseRes = await makeApiRequests({
        method: 'GET',
        endpoint: ENDPOINTS.COURSE_WITH_ID(courseId)
      });
      if (cancelled) return;
      if (courseRes.error) {
        setDetailLoading(false);
        toast.error(courseRes.error);
        setLocalCourse(null);
        return;
      }
      const modsRes = await makeApiRequests({
        endpoint: ENDPOINTS.COURSE_MODULES_SEARCH(courseId),
        requestBody: { sort: { order: 1 } }
      });
      if (cancelled) return;
      if (modsRes.error) {
        setDetailLoading(false);
        toast.error(modsRes.error);
        setLocalCourse(null);
        return;
      }
      const modList = modsRes.response.results || [];
      const modulesWithLessons = await Promise.all(
        modList.map(async m => {
          const lesRes = await makeApiRequests({
            endpoint: ENDPOINTS.COURSE_MODULE_LESSONS_SEARCH(String(m._id)),
            requestBody: { sort: { order: 1 } }
          });
          return { ...m, lessons: lesRes.error ? [] : lesRes.response.results || [] };
        })
      );
      if (cancelled) return;
      setDetailLoading(false);
      const response = {
        ...courseRes.response,
        modules: modulesWithLessons,
        allowedRoles: courseRes.response.allowedRoles || []
      };
      setLocalCourse(response);
      setCourseRevision(r => r + 1);
      if (onCourseDetailLoaded) onCourseDetailLoaded(response);
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, onCourseDetailLoaded]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const previous = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width
    };
    const scrollY = window.scrollY || window.pageYOffset || 0;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    if (!editMode) {
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    }

    let startX = 0;
    let startY = 0;
    const onTouchStart = event => {
      startX = event.touches[0]?.clientX ?? 0;
      startY = event.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = event => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      if (Math.abs(deltaX) > Math.abs(deltaY)) return;
      const rawTarget = event.target;
      const element = rawTarget instanceof Element ? rawTarget : rawTarget?.parentElement;
      if (!element) {
        event.preventDefault();
        return;
      }
      const scroller = element.closest('.course-player-scroll');
      if (!scroller || !shell.contains(scroller)) {
        event.preventDefault();
        return;
      }
      const atTop = scroller.scrollTop <= 0;
      const atBottom = scroller.scrollHeight - scroller.scrollTop <= scroller.clientHeight + 1;
      if ((deltaY > 0 && atTop) || (deltaY < 0 && atBottom)) {
        event.preventDefault();
      }
    };

    if (!editMode) {
      document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
      document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
    }

    return () => {
      html.style.overflow = previous.htmlOverflow;
      body.style.overflow = previous.bodyOverflow;
      html.style.overscrollBehavior = previous.htmlOverscroll;
      body.style.overscrollBehavior = previous.bodyOverscroll;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.left = previous.bodyLeft;
      body.style.right = previous.bodyRight;
      body.style.width = previous.bodyWidth;
      if (!editMode) window.scrollTo(0, scrollY);
      document.removeEventListener('touchstart', onTouchStart, { capture: true });
      document.removeEventListener('touchmove', onTouchMove, { capture: true });
    };
  }, [detailLoading, courseId, editMode]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 992px)');
    const onChange = () => setIsLargeViewport(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  useEffect(() => {
    setLessonSaveBaseline('');
  }, [courseId]);

  useEffect(() => {
    if (!editMode) {
      setLessonSaveBaseline('');
      return;
    }
    if (detailLoading || !currentLessonId) return;
    let lesson = null;
    let mod = null;
    for (const m of modules) {
      const found = m.lessons?.find(l => String(l._id ?? l.id ?? '') === currentLessonId);
      if (found) {
        lesson = found;
        mod = m;
        break;
      }
    }
    if (!lesson || !mod) return;
    setLessonSaveBaseline(
      JSON.stringify({
        lessonId: String(lesson._id ?? lesson.id ?? ''),
        title: lesson.title ?? '',
        description: lesson.description ?? '',
        videoUrl: lesson.videoUrl ?? '',
        platform: lesson.platform ?? '',
        audio: serializeLessonAudioForBaseline(lesson.audio),
        attachments: [...(lesson.attachments || [])]
          .map(a => (a != null && typeof a === 'object' ? String(a._id ?? a.id ?? '') : String(a ?? '')))
          .filter(Boolean)
          .sort(),
        moduleTitle: mod.title ?? '',
        moduleDescription: mod.description ?? ''
      })
    );
  }, [editMode, detailLoading, currentLessonId, courseRevision]);

  useEffect(() => {
    setEditingModuleId(null);
  }, [courseId]);

  useEffect(() => {
    if (editingModuleId && !modules.some(m => String(m._id ?? m.id ?? '') === editingModuleId)) {
      setEditingModuleId(null);
    }
  }, [modules, editingModuleId]);

  useEffect(() => {
    if (!courseId || !mergedCourse || detailLoading) return;
    const first = mergedCourse.modules?.[0]?.lessons?.[0];
    if (first) {
      const firstId = String(first._id ?? first.id ?? '');
      setCurrentLessonId(prev => {
        const stillValid = mergedCourse.modules.some(m =>
          (m.lessons || []).some(l => String(l._id ?? l.id ?? '') === prev)
        );
        return stillValid ? prev : firstId;
      });
    }
    if (mergedCourse.modules?.length) setExpandedModules(mergedCourse.modules.map(m => String(m._id ?? m.id ?? '')));
  }, [courseId, mergedCourse, detailLoading]);

  useEffect(() => {
    if (!courseId || editMode) return;
    let cancelled = false;
    (async () => {
      const { response, error } = await makeApiRequests({
        method: 'GET',
        endpoint: ENDPOINTS.COURSE_PROGRESS(courseId)
      });
      if (cancelled || error) return;
      const ids = (response?.completedLessons || []).map(x =>
        x && typeof x === 'object' && x._id != null ? String(x._id) : String(x)
      );
      setCompletedLessons(ids.filter(Boolean));
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId, editMode, modules]);

  useEffect(() => {
    if (!currentLesson || !modules.length) return;
    const exists = modules.some(m => m.lessons.some(l => String(l._id ?? l.id ?? '') === currentLessonId));
    if (!exists) {
      const fallback = modules[0]?.lessons[0];
      if (fallback) setCurrentLessonId(String(fallback._id ?? fallback.id ?? ''));
    }
  }, [modules, currentLessonId, currentLesson]);

  useLayoutEffect(() => {
    if (!editMode) return;
    const root = lessonQuillWrapRef.current;
    if (!root) return;
    const toolbar = root.querySelector('.ql-toolbar');
    const container = root.querySelector('.ql-container');
    const editor = root.querySelector('.ql-editor');
    if (toolbar) {
      Object.assign(toolbar.style, {
        border: '1px solid #6c757d',
        borderBottom: '1px solid #adb5bd',
        borderRadius: '6px 6px 0 0',
        backgroundColor: '#f8f9fa'
      });
    }
    if (container) {
      Object.assign(container.style, {
        border: '1px solid #6c757d',
        borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        minHeight: '7rem',
        fontSize: '0.9375rem'
      });
    }
    if (editor) {
      Object.assign(editor.style, { minHeight: '5.5rem' });
    }
  }, [editMode, currentLessonId]);

  if (courseId && detailLoading) {
    return (
      <div className="p-5 d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" className="mr-2" />
        <span>Loading course…</span>
      </div>
    );
  }

  if (courseId && !mergedCourse) {
    return (
      <div className="p-4">
        <Alert variant="warning">Course not found.</Alert>
        <Button variant="primary" onClick={handleExitEditor}>
          Back to courses
        </Button>
      </div>
    );
  }

  return (
    <>
      <div ref={shellRef} className="course-player-shell d-flex flex-column flex-lg-row bg-light">
        <CourseTrainingEditorSidebar
          isLargeViewport={isLargeViewport}
          displayCourseTitle={displayCourseTitle}
          editMode={editMode}
          completedCount={completedCount}
          totalLessons={totalLessons}
          progressPercentage={progressPercentage}
          modules={modules}
          expandedModules={expandedModules}
          toggleModule={toggleModule}
          currentLesson={currentLesson}
          onSelectLesson={handleSelectLesson}
          completedLessons={completedLessons}
          editingModuleId={editingModuleId}
          setEditingModuleId={setEditingModuleId}
          updateModuleTitle={updateModuleTitle}
          onRequestDeleteModule={(moduleId, label) => setDeleteConfirm({ type: 'module', moduleId, label })}
          onRequestDeleteLesson={(moduleId, lessonId, label) =>
            setDeleteConfirm({ type: 'lesson', moduleId, lessonId, label })
          }
          addLesson={addLesson}
          addModule={addModule}
          addingModule={addingModule}
          addingLessonModuleId={addingLessonModuleId}
          reorderingStructure={reorderingStructure}
          onStructureDragEnd={onStructureDragEnd}
          onExitEditor={handleExitEditor}
        />
        <CourseTrainingLessonView
          isLargeViewport={isLargeViewport}
          courseId={courseId}
          mergedCourse={mergedCourse}
          onExitEditor={handleExitEditor}
          editMode={editMode}
          saveCurrentLesson={saveCurrentLesson}
          savingLesson={savingLesson}
          lessonIsDirty={lessonIsDirty}
          currentLesson={currentLesson}
          currentModuleId={currentModuleId}
          canPlay={canPlay}
          playUrl={playUrl}
          isLessonVideoCourseAttachmentStream={isLessonVideoCourseAttachmentStream}
          canPlayAudio={canPlayAudio}
          audioPlayUrl={audioPlayUrl}
          isLessonAudioCourseAttachmentStream={isLessonAudioCourseAttachmentStream}
          updateLessonField={updateLessonField}
          updateLessonAudioUrl={updateLessonAudioUrl}
          lessonQuillWrapRef={lessonQuillWrapRef}
          lessonVideoFileInputRef={lessonVideoFileInputRef}
          lessonAudioFileInputRef={lessonAudioFileInputRef}
          uploadingLessonVideo={uploadingLessonVideo}
          uploadingLessonAudio={uploadingLessonAudio}
          uploadingLessonAttachment={uploadingLessonAttachment}
          uploadLessonVideoFile={uploadLessonVideoFile}
          uploadLessonAudioFile={uploadLessonAudioFile}
          clearLessonAudio={clearLessonAudio}
          visibleAttachments={visibleAttachments}
          videoAttachmentFileId={videoAttachmentFileId}
          audioAttachmentFileId={audioAttachmentFileId}
          downloadAttachment={downloadAttachment}
          removeAttachment={removeAttachment}
          addAttachmentFile={addAttachmentFile}
          lessonMarkedComplete={lessonMarkedComplete}
          togglingLessonComplete={togglingLessonComplete}
          toggleLessonComplete={toggleLessonComplete}
        />
      </div>

      <Prompt
        when={editMode && lessonIsDirty}
        message={location => {
          if (navGuardRef.current.skip) {
            navGuardRef.current.skip = false;
            return true;
          }
          if (navGuardRef.current.pending) return false;
          setPendingNav({ location });
          return false;
        }}
      />

      <Modal
        show={pendingNavigation != null}
        onHide={handleUnsavedNavigationCancel}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton={!savingLesson}>
          <Modal.Title>
            <h6 className="mb-0">Unsaved changes</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">You have unsaved changes to this lesson. Save before leaving?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" disabled={savingLesson} onClick={handleUnsavedNavigationCancel}>
            Cancel
          </Button>
          <Button variant="outline-danger" size="sm" disabled={savingLesson} onClick={handleUnsavedNavigationDiscard}>
            Don&apos;t save
          </Button>
          <Button variant="primary" size="sm" disabled={savingLesson} onClick={handleUnsavedNavigationSave}>
            {savingLesson ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="mr-2" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <AlertModal
        show={deleteConfirm != null}
        alertText={
          deleteConfirm?.type === 'module'
            ? `Delete module "${deleteConfirm.label}"? This permanently removes the module and all lessons inside it.`
            : deleteConfirm?.type === 'lesson'
            ? `Delete lesson "${deleteConfirm.label}"? This cannot be undone.`
            : ''
        }
        dismissButtonText="Cancel"
        dismissButtonVariant="secondary"
        continueButtonText="Delete"
        continueButtonVariant="danger"
        showProgress={deleteSubmitting}
        progressText={deleteConfirm?.type === 'module' ? 'Deleting module…' : 'Deleting lesson…'}
        onDismissClick={() => {
          if (!deleteSubmitting) setDeleteConfirm(null);
        }}
        onHide={() => {
          if (!deleteSubmitting) setDeleteConfirm(null);
        }}
        onContinueClick={handleConfirmDeleteTarget}
      />
    </>
  );
}

export default CourseTrainingEditor;
